import { useState, useMemo, useEffect, useCallback } from "react";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Warning, Plus, Download, FileArrowDown, ArrowsClockwise,
  MagnifyingGlass, Funnel, Envelope,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

import { useData } from "../hooks/useData";
import { useLocks } from "../hooks/useLocks";
import {
  Sidebar, DataTable, HistoryModal, DeleteConfirmDialog, GlobalOverlay,
  FilterDialog, UploadDialog, PasswordDialog, OwnPasswordDialog,
  AddEntryDialog, EditEntryDialog, ColumnConfigDialog, ImportDialog,
  UserManagementDialog, SettingsDialog, TrashDialog,
  SmtpConfigDialog, SendEmailDialog, InitialSetupDialog,
} from "../components/dashboard";

export default function DashboardPage() {
  const { user, sessionId, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isAdmin = user?.role === "admin";

  // --- Data hook ---
  const {
    data, loading, licenseTypes, columns, columnConfig, sourceStatus,
    appSettings, editingTitle, setEditingTitle, sorting,
    uploadType, setUploadType, uploading,
    MANNSCHAFT_OPTIONS, LEHRGANG_OPTIONS,
    fetchData, fetchSettings, saveInlineTitle,
    handleExport, handleFileUpload, handleClearTable, handleSort,
  } = useData(sessionId);

  // --- UI state ---
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "nachname", direction: "asc" });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [globalOverlay, setGlobalOverlay] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [historyModalRow, setHistoryModalRow] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Filters
  const [filterGeschlecht, setFilterGeschlecht] = useState("ALLE");
  const [filterSpieler, setFilterSpieler] = useState("ALLE");
  const [filterSR, setFilterSR] = useState("ALLE");
  const [filterGueltigBis, setFilterGueltigBis] = useState("ALLE");
  const [filterMannschaft, setFilterMannschaft] = useState("ALLE");
  const filters = { geschlecht: filterGeschlecht, spieler: filterSpieler, sr: filterSR, gueltigBis: filterGueltigBis, mannschaft: filterMannschaft };
  const setFilter = (key, value) => {
    const setters = { geschlecht: setFilterGeschlecht, spieler: setFilterSpieler, sr: setFilterSR, gueltigBis: setFilterGueltigBis, mannschaft: setFilterMannschaft };
    setters[key]?.(value);
  };

  // Dialog visibility
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showOwnPasswordDialog, setShowOwnPasswordDialog] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [showTrashDialog, setShowTrashDialog] = useState(false);
  const [showSmtpConfig, setShowSmtpConfig] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);

  // --- Notification handler (toasts for other users' actions) ---
  const handleNotification = useCallback((notification) => {
    if (notification.triggered_by !== user?.username) {
      toast.info(notification.message);
    }
  }, [user?.username]);

  // --- Locks hook ---
  const { rowLocks, acquireLock, releaseLock, syncVersion } = useLocks(sessionId, editEntry, fetchData, handleNotification);

  // --- Helpers ---
  const openWithSpinner = (key, setter) => {
    setLoadingAction(key);
    setTimeout(() => { setter(true); setLoadingAction(null); }, 300);
  };

  const showOverlayThen = (action) => {
    setGlobalOverlay(true);
    const safetyTimer = setTimeout(() => setGlobalOverlay(false), 3000);
    setTimeout(() => {
      try { action(); } catch (e) { console.error("Action error:", e); } finally { clearTimeout(safetyTimer); setGlobalOverlay(false); }
    }, 400);
  };

  // Toast duration from settings (in seconds, convert to ms)
  const toastDuration = (appSettings?.toast_duration || 3) * 1000;

  // Click-to-dismiss toasts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.closest('[data-sonner-toast]')) toast.dismiss();
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // --- Filtered + sorted data ---
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data];
    if (filterGeschlecht !== "ALLE") filtered = filtered.filter((r) => r.geschlecht === filterGeschlecht);
    if (filterSpieler === "OHNE") filtered = filtered.filter((r) => !r.spieler_lizenz);
    else if (filterSpieler !== "ALLE") filtered = filtered.filter((r) => r.spieler_lizenz === filterSpieler);
    if (filterSR === "OHNE") filtered = filtered.filter((r) => !r.schiedsrichter_lizenz);
    else if (filterSR !== "ALLE") filtered = filtered.filter((r) => r.schiedsrichter_lizenz === filterSR);
    if (filterGueltigBis !== "ALLE") filtered = filtered.filter((r) => r.gueltig_bis && r.gueltig_bis.endsWith(filterGueltigBis));
    if (filterMannschaft === "OHNE") filtered = filtered.filter((r) => !r.mannschaft);
    else if (filterMannschaft !== "ALLE") filtered = filtered.filter((r) => r.mannschaft === filterMannschaft);
    if (searchTerm) { const t = searchTerm.toLowerCase(); filtered = filtered.filter((r) => Object.entries(r).some(([k, v]) => k !== '_id' && k !== '_history' && String(v).toLowerCase().includes(t))); }
    if (sortConfig.key) filtered = [...filtered].sort((a, b) => { const av = String(a[sortConfig.key] || "").toLowerCase(); const bv = String(b[sortConfig.key] || "").toLowerCase(); if (av < bv) return sortConfig.direction === "asc" ? -1 : 1; if (av > bv) return sortConfig.direction === "asc" ? 1 : -1; return 0; });
    return filtered;
  }, [data, searchTerm, sortConfig, filterGeschlecht, filterSpieler, filterSR, filterGueltigBis, filterMannschaft]);

  // --- Row actions ---
  const doSort = (key) => {
    handleSort(key);
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  };

  const handleEditEntry = async (rowId, rowData) => {
    const locked = await acquireLock(rowId);
    if (!locked) return;
    setEditEntry({ rowId, data: rowData });
  };

  const handleDeleteEntry = (rowId, rowData) => {
    setConfirmDelete({ rowId, name: `${rowData.vorname || ''} ${rowData.nachname || ''}`.trim() });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    const name = confirmDelete.name;
    setConfirmDelete(null);
    setGlobalOverlay(true);
    try { await axios.delete(`${API}/data/delete/${confirmDelete.rowId}?session_id=${sessionId}`); toast.success(`${name} - gelöscht (von ${user.username})`); await fetchData(); await syncVersion(); } catch (err) { toast.error("Fehler beim Löschen"); console.error(err); } finally { setGlobalOverlay(false); }
  };

  const handleRestoreEntry = async (rowId) => {
    setLoadingAction(`restore-${rowId}`);
    try { const res = await axios.post(`${API}/data/restore/${rowId}?session_id=${sessionId}`); toast.success(`${res.data.name || "Eintrag"} - wiederhergestellt (von ${user.username})`); await fetchData(); await syncVersion(); } catch (err) { toast.error("Fehler beim Wiederherstellen"); console.error(err); } finally { setLoadingAction(null); }
  };

  const handleUndoChange = async (rowId, historyIndex) => {
    setLoadingAction(`undo-${rowId}-${historyIndex}`);
    try { const res = await axios.post(`${API}/data/undo-change/${rowId}/${historyIndex}?session_id=${sessionId}`); toast.success(`${res.data.message || "Änderung rückgängig gemacht"} (von ${user.username})`); await fetchData(); await syncVersion(); } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); } finally { setLoadingAction(null); }
  };

  const handleLehrgangChange = async (rowId, row, newValue) => {
    const locked = await acquireLock(rowId);
    if (!locked) return;
    setLoadingAction(`lehrgang-${rowId}`);
    try {
      await axios.put(`${API}/data/update?session_id=${sessionId}`, {
        row_id: rowId, vorname: row.vorname, nachname: row.nachname,
        geburtsdatum: row.geburtsdatum || "", geschlecht: row.geschlecht || "",
        spieler_lizenz: row.spieler_lizenz || "", schiedsrichter_lizenz: row.schiedsrichter_lizenz || "",
        gueltig_ab: row.gueltig_ab || "", gueltig_bis: row.gueltig_bis || "",
        aktiv: row.aktiv || "", mannschaft: row.mannschaft || "",
        lehrgang: newValue === "_none" ? "" : newValue,
      });
      const label = newValue === "_none" ? "entfernt" : `gesetzt: ${newValue}`;
      toast.success(`${row.vorname} ${row.nachname} - Lehrgang ${label} (von ${user.username})`);
      await fetchData();
      await syncVersion();
    } catch (err) { toast.error("Fehler beim Ändern"); console.error(err); } finally { setLoadingAction(null); await releaseLock(rowId); }
  };

  const handleMannschaftChange = async (rowId, row, newValue) => {
    const locked = await acquireLock(rowId);
    if (!locked) return;
    setLoadingAction(`mannschaft-${rowId}`);
    try {
      await axios.put(`${API}/data/update?session_id=${sessionId}`, {
        row_id: rowId, vorname: row.vorname, nachname: row.nachname,
        geburtsdatum: row.geburtsdatum || "", geschlecht: row.geschlecht || "",
        spieler_lizenz: row.spieler_lizenz || "", schiedsrichter_lizenz: row.schiedsrichter_lizenz || "",
        gueltig_ab: row.gueltig_ab || "", gueltig_bis: row.gueltig_bis || "",
        aktiv: row.aktiv || "", mannschaft: newValue === "_none" ? "" : newValue,
        lehrgang: row.lehrgang || "",
      });
      const label2 = newValue === "_none" ? "entfernt" : `gesetzt: ${newValue}`;
      toast.success(`${row.vorname} ${row.nachname} - Mannschaft ${label2} (von ${user.username})`);
      await fetchData();
      await syncVersion();
    } catch (err) { toast.error("Fehler beim Ändern"); console.error(err); } finally { setLoadingAction(null); await releaseLock(rowId); }
  };

  const activeFilterCount = [filterGeschlecht !== "ALLE", filterSpieler !== "ALLE", filterSR !== "ALLE", filterGueltigBis !== "ALLE", filterMannschaft !== "ALLE"].filter(Boolean).length;

  // --- RENDER ---
  return (
    <div className="flex h-screen overflow-hidden noise-bg">
      <Sidebar
        user={user} isAdmin={isAdmin} theme={theme} toggleTheme={toggleTheme} logout={logout}
        sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}
        appSettings={appSettings} editingTitle={editingTitle} setEditingTitle={setEditingTitle} saveInlineTitle={saveInlineTitle}
        loadingAction={loadingAction} openWithSpinner={openWithSpinner}
        setShowUploadDialog={setShowUploadDialog} setShowPasswordDialog={setShowPasswordDialog}
        setShowOwnPasswordDialog={setShowOwnPasswordDialog} setShowUserManagement={setShowUserManagement}
        setShowColumnConfig={setShowColumnConfig} setShowSettingsDialog={setShowSettingsDialog}
        setShowTrashDialog={setShowTrashDialog} handleClearTable={handleClearTable}
        setShowSmtpConfig={setShowSmtpConfig}
      />

      <main className="flex-1 flex flex-col h-screen overflow-hidden p-2 sm:p-3 pr-2 sm:pr-4">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
          {isAdmin && editingTitle === 'dashboard' ? (
            <input type="text" defaultValue={appSettings.dashboard_title} autoFocus
              className="text-sm sm:text-base md:text-lg font-black uppercase tracking-tight bg-transparent border-b border-primary outline-none flex-shrink-0"
              onBlur={(e) => saveInlineTitle('dashboard_title', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveInlineTitle('dashboard_title', e.target.value); if (e.key === 'Escape') setEditingTitle(null); }}
            />
          ) : (
            <h2
              className={`text-sm sm:text-base md:text-lg font-black uppercase tracking-tight flex-shrink-0 ${isAdmin ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
              onClick={() => isAdmin && setEditingTitle('dashboard')}
              title={isAdmin ? "Klicken zum Bearbeiten" : undefined}
            >{appSettings.dashboard_title}</h2>
          )}
          {data.length === 0 && (!sourceStatus.spieler_exists || !sourceStatus.schiedsrichter_exists) && (
            <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-700 dark:text-amber-400 text-[10px] sm:text-xs">
              <Warning className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" weight="fill" />
              <span className="hidden xs:inline">
                Fehlende CSV:{!sourceStatus.spieler_exists && <strong className="ml-1">Spieler</strong>}
                {!sourceStatus.spieler_exists && !sourceStatus.schiedsrichter_exists && ", "}
                {!sourceStatus.schiedsrichter_exists && <strong className="ml-1">Schiedsrichter</strong>}
              </span>
              <span className="xs:hidden">CSV fehlt</span>
            </div>
          )}
          <div className="flex items-center gap-1 sm:gap-2 ml-auto flex-shrink-0">
            <Button data-testid="add-entry-button" size="sm" onClick={() => openWithSpinner('open-add', setShowAddDialog)} className="h-7 sm:h-8 bg-primary text-xs sm:text-sm px-2 sm:px-3">
              {loadingAction === 'open-add' ? <div className="animate-spin h-3 w-3 sm:h-4 sm:w-4 border-2 border-primary-foreground border-t-transparent rounded-full mr-1" /> : <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />}
              <span className="hidden sm:inline">Hinzufügen</span><span className="sm:hidden">Neu</span>
            </Button>
            <Button data-testid="export-button" variant="outline" onClick={() => handleExport(filters, sortConfig)} disabled={data.length === 0} size="sm" className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3">
              <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" /><span className="hidden sm:inline">Export</span>
            </Button>
            <Button data-testid="send-email-button" variant="outline" onClick={() => setShowSendEmail(true)} disabled={data.length === 0} size="sm" className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3">
              <Envelope className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" /><span className="hidden sm:inline">E-Mail</span>
            </Button>
            {isAdmin && <Button data-testid="import-button" variant="ghost" size="sm" onClick={() => setShowImportDialog(true)} className="h-7 sm:h-8 w-7 sm:w-8 p-0" title="Import"><FileArrowDown className="h-3 w-3 sm:h-4 sm:w-4" /></Button>}
            <Button data-testid="refresh-button" variant="ghost" size="sm" onClick={fetchData} disabled={loading} className="h-7 sm:h-8 w-7 sm:w-8 p-0" title="Aktualisieren">
              <ArrowsClockwise className={`h-3 w-3 sm:h-4 sm:w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats + Search + Filters */}
        <div className="flex-shrink-0 mb-2 flex items-end gap-1 sm:gap-2 flex-wrap">
          <div className="flex gap-1 sm:gap-2 flex-shrink-0">
            <div className="bg-card border border-border rounded px-1.5 sm:px-2 py-0.5 sm:py-1 min-w-[45px] sm:min-w-[60px]">
              <p className="text-[7px] sm:text-[8px] uppercase tracking-wider font-semibold text-muted-foreground">Gesamt</p>
              <p className="text-xs sm:text-sm font-black">{data.length}</p>
            </div>
            <div className="bg-card border border-border rounded px-1.5 sm:px-2 py-0.5 sm:py-1 min-w-[45px] sm:min-w-[60px]">
              <p className="text-[7px] sm:text-[8px] uppercase tracking-wider font-semibold text-muted-foreground">Spieler</p>
              <p className="text-xs sm:text-sm font-black">{data.filter(d => d.spieler_lizenz).length}</p>
            </div>
            <div className="bg-card border border-border rounded px-1.5 sm:px-2 py-0.5 sm:py-1 min-w-[40px] sm:min-w-[50px]">
              <p className="text-[7px] sm:text-[8px] uppercase tracking-wider font-semibold text-muted-foreground">SR</p>
              <p className="text-xs sm:text-sm font-black">{data.filter(d => d.schiedsrichter_lizenz).length}</p>
            </div>
          </div>
          <div className="h-6 sm:h-8 w-px bg-border mx-0.5 sm:mx-1 flex-shrink-0 hidden sm:block" />
          <div className="flex-shrink-0">
            <p className="text-[8px] sm:text-[9px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">Suche</p>
            <div className="relative w-20 sm:w-32">
              <MagnifyingGlass className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 h-2.5 w-2.5 sm:h-3 sm:w-3 text-muted-foreground" />
              <Input data-testid="search-input" type="text" placeholder="..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-5 sm:pl-6 h-5 sm:h-6 text-[10px] sm:text-xs" />
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => openWithSpinner('open-filter', setShowFilterDialog)} className="flex-shrink-0 h-5 sm:h-6 px-2 text-[10px] sm:text-xs xlx:hidden" data-testid="filter-button-mobile">
            {loadingAction === 'open-filter' ? <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full mr-1" /> : <Funnel className="h-3 w-3 mr-1" />}
            Filter{activeFilterCount > 0 && <span className="ml-1 px-1 bg-primary text-primary-foreground rounded-full text-[8px]">{activeFilterCount}</span>}
          </Button>
          {/* Inline filters (> 1280px) */}
          <InlineFilter label="Geschlecht" testId="filter-geschlecht-select" value={filterGeschlecht} onChange={setFilterGeschlecht} options={licenseTypes.geschlechter} width="w-24" />
          <InlineFilter label="Spieler-Lizenz" testId="filter-spieler-select" value={filterSpieler} onChange={setFilterSpieler} options={licenseTypes.spieler_lizenzen} width="w-28" withOhne />
          <InlineFilter label="Mannschaft" testId="filter-mannschaft-select" value={filterMannschaft} onChange={setFilterMannschaft} options={MANNSCHAFT_OPTIONS} width="w-24" withOhne />
          <InlineFilter label="SR-Lizenz" testId="filter-sr-select" value={filterSR} onChange={setFilterSR} options={licenseTypes.schiedsrichter_lizenzen} width="w-28" withOhne />
          <InlineFilter label="Gültig bis" testId="filter-gueltig-bis-select" value={filterGueltigBis} onChange={setFilterGueltigBis} options={licenseTypes.gueltig_bis_jahre} width="w-24" />
          <span className="text-[9px] sm:text-[10px] text-muted-foreground ml-auto flex-shrink-0 self-end pb-0.5 sm:pb-1">
            {filteredAndSortedData.length}/{data.length}
          </span>
        </div>

        {/* Table */}
        <DataTable
          columns={columns} data={filteredAndSortedData} allData={data}
          loading={loading} sorting={sorting} sortConfig={sortConfig} handleSort={doSort}
          rowLocks={rowLocks} user={user} loadingAction={loadingAction} isAdmin={isAdmin}
          LEHRGANG_OPTIONS={LEHRGANG_OPTIONS} MANNSCHAFT_OPTIONS={MANNSCHAFT_OPTIONS}
          handleLehrgangChange={handleLehrgangChange} handleMannschaftChange={handleMannschaftChange}
          handleEditEntry={handleEditEntry} handleDeleteEntry={handleDeleteEntry}
          handleRestoreEntry={handleRestoreEntry}
          showOverlayThen={showOverlayThen} setGlobalOverlay={setGlobalOverlay}
          setHistoryModalRow={setHistoryModalRow} setShowUploadDialog={setShowUploadDialog}
          searchTerm={searchTerm}
        />

        <HistoryModal historyModalRow={historyModalRow} data={data} loadingAction={loadingAction} handleUndoChange={handleUndoChange} onClose={() => setHistoryModalRow(null)} />
      </main>

      {/* Dialogs */}
      <UploadDialog open={showUploadDialog} onOpenChange={setShowUploadDialog} uploadType={uploadType} setUploadType={setUploadType} onUpload={handleFileUpload} uploading={uploading} />
      <FilterDialog open={showFilterDialog} onOpenChange={setShowFilterDialog} filters={filters} setFilter={setFilter} licenseTypes={licenseTypes} MANNSCHAFT_OPTIONS={MANNSCHAFT_OPTIONS} />
      <PasswordDialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog} sessionId={sessionId} API={API} />
      <OwnPasswordDialog open={showOwnPasswordDialog} onOpenChange={setShowOwnPasswordDialog} sessionId={sessionId} API={API} userEmail={user?.email || ""} />
      <AddEntryDialog open={showAddDialog} onOpenChange={setShowAddDialog} sessionId={sessionId} licenseTypes={licenseTypes} onSuccess={fetchData} API={API} mannschaftOptions={MANNSCHAFT_OPTIONS} lehrgangOptions={LEHRGANG_OPTIONS} />
      <EditEntryDialog open={editEntry !== null} onOpenChange={(open) => { if (!open) { if (editEntry) releaseLock(editEntry.rowId); setEditEntry(null); } }} sessionId={sessionId} licenseTypes={licenseTypes} editEntry={editEntry} onSuccess={fetchData} API={API} mannschaftOptions={MANNSCHAFT_OPTIONS} lehrgangOptions={LEHRGANG_OPTIONS} username={user?.username} />
      <ColumnConfigDialog open={showColumnConfig} onOpenChange={setShowColumnConfig} sessionId={sessionId} columnConfig={columnConfig} onSuccess={fetchData} API={API} />
      <ImportDialog open={showImportDialog} onOpenChange={setShowImportDialog} sessionId={sessionId} onSuccess={fetchData} API={API} />
      <UserManagementDialog open={showUserManagement} onOpenChange={setShowUserManagement} sessionId={sessionId} API={API} />
      <SettingsDialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog} sessionId={sessionId} API={API} onSuccess={fetchSettings} />
      <TrashDialog open={showTrashDialog} onOpenChange={setShowTrashDialog} sessionId={sessionId} API={API} onSuccess={fetchData} />
      <SmtpConfigDialog open={showSmtpConfig} onOpenChange={setShowSmtpConfig} sessionId={sessionId} API={API} />
      <SendEmailDialog open={showSendEmail} onOpenChange={setShowSendEmail} sessionId={sessionId} API={API} filters={filters} sortConfig={sortConfig} />

      <DeleteConfirmDialog confirmDelete={confirmDelete} setConfirmDelete={setConfirmDelete} executeDelete={executeDelete} />
      <GlobalOverlay visible={globalOverlay} />
      <Toaster position="bottom-right" richColors closeButton duration={toastDuration} />

      <InitialSetupDialog
        open={user?.must_change_password === true}
        sessionId={sessionId}
        API={API}
        currentEmail={user?.email || ""}
        onComplete={logout}
      />
    </div>
  );
}

function InlineFilter({ label, testId, value, onChange, options, width, withOhne }) {
  return (
    <div className="flex-shrink-0 hidden xlx:block">
      <p className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className={`${width} h-6 text-xs`}><SelectValue placeholder="Alle" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ALLE">Alle</SelectItem>
          {withOhne && <SelectItem value="OHNE">Ohne</SelectItem>}
          {options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
